/**
 * The tier table — the one place the three plans of PLAN §9 are written down.
 *
 * **This module is shared with the Worker.** `worker/entitlement.ts` imports the
 * limits from here rather than restating them, so a change to a number is a
 * change in one file and the client cannot drift from the server. It therefore
 * stays pure TypeScript: no React, no DOM, no imports at all. (The one mirror
 * that exists on purpose is `apps/marketing/src/plans.ts`, which is a separate
 * Vite project with no path to this one; its header says so.)
 *
 * ## What is a constant here and what is not
 *
 * The numbers below are the *defaults*. Every one of them is overridable per
 * deployment through the environment (see `worker/env.ts`), because a beta
 * cohort with a different cap must not need a client deploy. So the client
 * displays {@link Entitlement} — the numbers the server actually granted this
 * user — and falls back to the table only when it has not been told yet.
 *
 * The **prices are display-only**. Nothing in this repo takes money; billing
 * (Paddle, PLAN §9) arrives in M7 and will flip entitlement rows. Until then
 * every surface that shows a price also says billing is not live.
 *
 * ## Nothing here enforces anything
 *
 * The recipe, blend and journal caps are enforced server-side in the Durable Object at
 * apply time (`worker/entitlement.ts` says why), and the assistant allowance is
 * enforced in `worker/ai/routes.ts` before the model is asked. This module is
 * the client's copy for *display*, and for the one check that genuinely belongs
 * in the UI: telling a brewer, before they import a file with forty recipes in
 * it, that only three will fit.
 *
 * The deliberate non-use is the "New recipe" button. It does **not** pre-check
 * the cap. It dispatches, the optimistic recipe appears, the server refuses it,
 * and the existing reconciliation rolls it back with the upgrade dialog. That is
 * one code path instead of two, and the one that runs is the one that is
 * authoritative.
 *
 * That last paragraph holds for a **signed-in** workspace, and it hid a hole for
 * a signed-out one. A guest never talks to a server at all, so there was nothing
 * to refuse the eleventh recipe: the sidebar read "Free · 12 of 10 recipes" and
 * the import dialog promised to take the first two of a file and then took all
 * of it. So `store/workspace-context.tsx` installs `lib/plan-cap.ts` — the very
 * function the Durable Object runs — on the store while the identity is `null`,
 * and lifts it again the moment there is a user id. The rule is still written
 * once and judged in one place; a guest simply runs that place locally, because
 * locally is the only place they have.
 */

/** The plans PLAN §9 defines. `trial` is the absence of an entitlement row. */
export type Plan = 'trial' | 'brewmaster' | 'beerwright';

/** Every plan id, cheapest first. The order the pricing table is built in. */
export const PLAN_IDS = ['trial', 'brewmaster', 'beerwright'] as const;

/** One tier, as both the app and the marketing copy describe it. */
export interface PlanTier {
  readonly id: Plan;
  /** What a user is told they are on. Note `trial` displays as "Free". */
  readonly name: string;
  /** Yearly price, formatted. `null` for the free tier. */
  readonly yearlyPrice: string | null;
  /** One line under the name, in the pricing table. */
  readonly summary: string;
  /** Recipes this tier allows, or `null` for no cap. */
  readonly recipeLimit: number | null;
  /** Blending sessions this tier allows, or `null` for no cap. */
  readonly blendLimit: number | null;
  /** Journals this tier allows, or `null` for no cap (D-J10). */
  readonly journalLimit: number | null;
  /** Assistant messages per calendar month (UTC). */
  readonly aiMonthlyMessages: number;
  /** The comparison bullets, in the order a brewer cares about them. */
  readonly features: readonly string[];
}

/**
 * The three tiers.
 *
 * Edit the numbers and the strings here; every screen, the sidebar meter, the
 * upgrade dialog, the settings row and the Worker's limits follow. Competitor
 * anchors that set these prices are recorded in PLAN §9, not here — this is the
 * decision, not the argument for it.
 */
export const PLAN_TIERS: Readonly<Record<Plan, PlanTier>> = {
  trial: {
    id: 'trial',
    name: 'Free',
    yearlyPrice: null,
    summary: 'The studio, ten recipes, five blends, five journals, no card.',
    recipeLimit: 10,
    blendLimit: 5,
    journalLimit: 5,
    aiMonthlyMessages: 5,
    features: [
      '10 recipes',
      '5 blending sessions',
      '5 journals',
      'The full editor, style bars and calculation engine',
      'The whole water and mash-pH toolkit',
      'BeerXML and BeerJSON import, BeerJSON export',
      '5 assistant messages a month',
    ],
  },
  /**
   * Brewmaster: the three meters it lifts, and nothing else (PLAN-UX-PASS A4).
   *
   * The summary used to end "…and batches, on every device", and the bullets
   * used to carry "Unlimited batches, blends, journals, inventory and imports"
   * and "Sync across every device". Neither was true. `plan-cap.ts` holds
   * **three** rules — recipes, blends, journals — and they are the only things
   * `workspace-do.ts` asks the plan about, so batches, inventory and imports
   * are uncapped on the free tier too. Sync is the same story: `/api/sync`
   * reads a session and never a plan, so a free account already syncs across
   * every device it signs in on.
   *
   * The list is shorter than it was, and deliberately not topped back up: what
   * else this tier should advertise is the owner's call. Tasting sheets moved
   * down from Beerwright on 2026-10-11. It matches `apps/marketing/src/plans.ts`
   * — recipes, blends, journals, tasting sheets, assistant — so the pricing page
   * and the upgrade dialog say one thing.
   */
  brewmaster: {
    id: 'brewmaster',
    name: 'Brewmaster',
    yearlyPrice: '$14.99',
    summary: 'Unlimited recipes, blends and journals, and tasting sheets.',
    recipeLimit: null,
    blendLimit: null,
    journalLimit: null,
    aiMonthlyMessages: 15,
    features: [
      'Unlimited recipes',
      'Unlimited blends',
      'Unlimited journals',
      'Tasting cards, bottle labels, competition scoresheets and the feedback page',
      '15 assistant messages a month',
    ],
  },
  beerwright: {
    id: 'beerwright',
    name: 'Beerwright',
    yearlyPrice: '$29.99',
    summary: 'Everything in Brewmaster, plus your brewing measured, charted and asked about.',
    recipeLimit: null,
    blendLimit: null,
    journalLimit: null,
    aiMonthlyMessages: 200,
    features: [
      'Fermentation charts from your Tilt, RAPT Pill or iSpindel',
      'Your brewing charted: efficiency, attenuation and yield trends',
      '200 assistant messages a month',
      'Recipe generation, water advice and diffs on tap',
      'Generous fair-use allowance',
    ],
  },
};

/** The tiers as a list, cheapest first — what a three-column table maps over. */
export const PLAN_TABLE: readonly PlanTier[] = PLAN_IDS.map((id) => PLAN_TIERS[id]);

/** Recipes the free tier allows. PLAN §9, settled. */
export const RECIPE_LIMIT: number = PLAN_TIERS.trial.recipeLimit ?? 10;

/** Blending sessions the free tier allows. */
export const BLEND_LIMIT: number = PLAN_TIERS.trial.blendLimit ?? 5;

/** Journals the free tier allows (D-J10). */
export const JOURNAL_LIMIT: number = PLAN_TIERS.trial.journalLimit ?? 5;

/**
 * The cap that means "no cap".
 *
 * A large sentinel rather than `null` so the number travels through the socket
 * URL, the object's storage and the client's counter without any of them
 * needing a nullable branch. The UI turns it back into an absence in
 * {@link hasRecipeCap}.
 */
export const UNLIMITED_RECIPES = 1_000_000;

/** Above this, a cap is treated as "no cap" for display. */
const UNLIMITED_THRESHOLD = 100_000;

/**
 * A stored or wire plan id, including the name `brewer` used before
 * `migrations/0011_plan_ids.sql` lined the ids up with the labels.
 *
 * Anything else this build does not sell — including the retired `pro` — is
 * `null`, which callers turn into the free tier.
 */
export function parsePlan(value: unknown): Plan | null {
  if (value === 'trial' || value === 'brewmaster' || value === 'beerwright') return value;
  if (value === 'brewer') return 'brewmaster';
  return null;
}

/** Whether an arbitrary value is one of this build's three plan ids. */
export function isPlan(value: unknown): value is Plan {
  return value === 'trial' || value === 'brewmaster' || value === 'beerwright';
}

/** The tier for a plan id. */
export function planTier(plan: Plan): PlanTier {
  return PLAN_TIERS[plan];
}

/** `Brewmaster` — what the user is told they are on. */
export function planName(plan: Plan): string {
  return PLAN_TIERS[plan].name;
}

/** `$14.99/yr`, or `Free`. Display only — no money moves (PLAN §9). */
export function priceLine(tier: PlanTier): string {
  if (tier.yearlyPrice === null) return 'Free';
  return `${tier.yearlyPrice}/yr`;
}

/** What this account's plan allows. Mirrors the Worker's `Entitlement`. */
export interface Entitlement {
  readonly plan: Plan;
  readonly recipeLimit: number;
  /** Assistant messages per calendar month, as this deployment granted them. */
  readonly aiMonthlyLimit: number;
  /**
   * When this plan lapses to the free tier, in epoch **seconds**, or `null`.
   *
   * Only a redeemed trial code sets it (PLAN-LAUNCH B5). A bought subscription
   * carries `null`, and so does a grant that has already lapsed — the server
   * resolves an expired row to the free tier before it sends it, so the client
   * never has to reason about a date in the past.
   */
  readonly expiresAt: number | null;
  /**
   * Whether Paddle still has a subscription for this account (PLAN-UX-PASS A1).
   *
   * True for every status but the ones that mean Paddle is done with this
   * account: `canceled`, `refunded`, and no subscription at all. A status the
   * server does not recognise counts as live, on purpose. It is **not** an
   * entitlement: what the
   * account may do is the rest of this object, and a subscriber and a
   * code-holder on the same tier may do exactly the same things. It rides here
   * because it changes at the same moments the plan does, and the one screen
   * that reads it — the trial-code field, which would overwrite a paid plan —
   * already has this object to hand.
   *
   * `false` for a guest, for an offline boot, and for a Worker too old to send
   * it. All three fail in the direction that merely *offers* the code field to
   * somebody the server will then refuse, rather than hiding it from somebody
   * entitled to use it.
   */
  readonly subscribed: boolean;
}

/**
 * The entitlement assumed before `/api/me` answers, and for a guest.
 *
 * The free tier, not a paid one: an unknown plan must read as the cheapest, or
 * a slow network would briefly promise a user something the server will refuse.
 */
export const DEFAULT_ENTITLEMENT: Entitlement = {
  plan: 'trial',
  recipeLimit: RECIPE_LIMIT,
  aiMonthlyLimit: PLAN_TIERS.trial.aiMonthlyMessages,
  expiresAt: null,
  subscribed: false,
};

/** Whether this entitlement has a cap worth showing. */
export function hasRecipeCap(entitlement: Entitlement): boolean {
  if (PLAN_TIERS[entitlement.plan].recipeLimit === null) return false;
  return entitlement.recipeLimit < UNLIMITED_THRESHOLD;
}

/** How many more recipes will fit, or `null` when there is no cap. */
export function remainingRecipes(entitlement: Entitlement, used: number): number | null {
  if (!hasRecipeCap(entitlement)) return null;
  return Math.max(0, entitlement.recipeLimit - used);
}

/**
 * Blending sessions this plan may hold.
 *
 * Paid tiers are the same sentinel {@link UNLIMITED_RECIPES} uses for recipes,
 * so the Durable Object can compare a count against a number on both meters.
 * The free tier's five is not overridable the way the recipe cap is — a
 * support bump of recipes is still a free account, and blends stay at five.
 */
export function blendLimitFor(plan: Plan): number {
  return PLAN_TIERS[plan].blendLimit === null ? UNLIMITED_RECIPES : BLEND_LIMIT;
}

/**
 * Journals this plan may hold (D-J10).
 *
 * The blend cap's twin, down to the sentinel, so the Durable Object compares a
 * count against a number on all three meters. Five is the same number for the
 * same reason: it is enough to keep a mead, a cider and a couple of jars going
 * at once, and short of a free account holding a cellar.
 */
export function journalLimitFor(plan: Plan): number {
  return PLAN_TIERS[plan].journalLimit === null ? UNLIMITED_RECIPES : JOURNAL_LIMIT;
}

/**
 * `Free · 7 of 10 recipes`, or `Beerwright`.
 *
 * A capped plan names the meter that matters; an uncapped one is just its name.
 * The sidebar adds the assistant meter separately, and only when it is worth
 * looking at (see {@link aiMeterLabel}).
 */
export function planSummary(entitlement: Entitlement, used: number): string {
  const label = planName(entitlement.plan);
  if (!hasRecipeCap(entitlement)) return label;
  const recipes = used === 1 ? 'recipe' : 'recipes';
  return `${label} · ${used} of ${entitlement.recipeLimit} ${recipes}`;
}

/**
 * Read an entitlement out of an untrusted JSON body, falling back to the free tier.
 *
 * `expiresAt` is **optional**, and its absence is `null` rather than a reason to
 * reject the body: a deployment mid-rollout can serve a payload from before B5,
 * and "this plan does not expire" is both the safe reading and the true one for
 * every entitlement that is not a redeemed trial code.
 */
export function parseEntitlement(value: unknown): Entitlement {
  if (typeof value !== 'object' || value === null) return DEFAULT_ENTITLEMENT;
  const record = value as {
    plan?: unknown;
    recipeLimit?: unknown;
    aiMonthlyLimit?: unknown;
    expiresAt?: unknown;
    subscribed?: unknown;
  };
  const plan: Plan = parsePlan(record.plan) ?? 'trial';
  return {
    plan,
    recipeLimit: positive(record.recipeLimit) ?? PLAN_TIERS[plan].recipeLimit ?? UNLIMITED_RECIPES,
    aiMonthlyLimit: positive(record.aiMonthlyLimit) ?? PLAN_TIERS[plan].aiMonthlyMessages,
    expiresAt: positive(record.expiresAt),
    // Strictly `=== true`, so a missing field and a Worker that sends something
    // else both read as "no subscription" — see the field's own note on why
    // that is the safe direction to be wrong in.
    subscribed: record.subscribed === true,
  };
}

/**
 * `until March 2027`, or `null` when this plan does not lapse.
 *
 * **Settings' plan row only.** The sidebar's meter answers "how much of my
 * allowance is left", which an expiry date is not, and putting a countdown
 * beside a recipe count would turn an ambient status line into a nag. Settings
 * is where somebody goes to ask about their plan, so it is where the answer
 * belongs — beside the Upgrade button that resolves it.
 *
 * The month is deliberately coarser than the stored second. A trial that ends at
 * 14:32 on the 4th ends "in March" as far as a decision to subscribe goes, and a
 * precise timestamp reads as a deadline rather than as information.
 */
export function planExpiryLabel(entitlement: Entitlement): string | null {
  if (entitlement.expiresAt === null || !Number.isFinite(entitlement.expiresAt)) return null;
  const at = new Date(entitlement.expiresAt * 1000);
  const name = MONTH_NAMES[at.getUTCMonth()];
  return name === undefined ? null : `until ${name} ${at.getUTCFullYear()}`;
}

function positive(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

// ---------------------------------------------------------------------------
// The assistant allowance
// ---------------------------------------------------------------------------

/**
 * What the server says about this month's assistant usage.
 *
 * Reported by `GET /api/ai/usage` and by the 429 body, which carry the same
 * shape so the drawer's exhausted state and the sidebar's meter read one type.
 */
export interface AiAllowance {
  readonly plan: Plan;
  readonly used: number;
  readonly limit: number;
  /** `YYYY-MM`, UTC. The month the count belongs to. */
  readonly month: string;
  /** ISO instant the count returns to zero: midnight UTC on the 1st. */
  readonly resetsAt: string;
}

/** Read an allowance out of an untrusted JSON body. `null` when it is not one. */
export function parseAllowance(value: unknown): AiAllowance | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const plan = parsePlan(record.plan);
  if (plan === null || typeof record.month !== 'string') return null;
  const used = typeof record.used === 'number' && Number.isFinite(record.used) ? record.used : null;
  const limit = positive(record.limit);
  if (used === null || limit === null) return null;
  return {
    plan,
    used,
    limit,
    month: record.month,
    resetsAt: typeof record.resetsAt === 'string' ? record.resetsAt : '',
  };
}

/**
 * Fraction of the month's allowance spent, 0–1.
 *
 * Clamped, because a limit lowered mid-month (an env override, a downgrade) can
 * leave a count above it and a progress bar past its end is a bug on screen.
 */
export function allowanceUsedFraction(allowance: AiAllowance): number {
  if (allowance.limit <= 0) return 1;
  return Math.min(1, Math.max(0, allowance.used / allowance.limit));
}

/** Above this share of the monthly allowance, the sidebar starts showing it. */
export const AI_METER_VISIBLE_ABOVE = 0.5;

/**
 * `AI: 140/200 this month`, or `null` when it is not worth the pixels.
 *
 * Two conditions, and both are about not making anyone watch a number they did
 * not ask for:
 *
 * - **Only where the allowance is something the plan sells.** A permanent
 *   "AI: 3/5" on the free tier would read as a countdown to a paywall — the
 *   drawer says so when it runs out, which is the moment it matters. A plan
 *   whose allowance is *larger than the free tier's* is one the user is paying
 *   for, so its meter is information rather than pressure.
 * - **Only past half.** Below that there is nothing to plan around.
 */
export function aiMeterLabel(allowance: AiAllowance | null): string | null {
  if (allowance === null) return null;
  if (allowance.limit <= PLAN_TIERS.trial.aiMonthlyMessages) return null;
  if (allowanceUsedFraction(allowance) <= AI_METER_VISIBLE_ABOVE) return null;
  return `AI: ${allowance.used}/${allowance.limit} this month`;
}

// ---------------------------------------------------------------------------
// Upgrade prompts
// ---------------------------------------------------------------------------

/**
 * Why an upgrade dialog was raised. Decides which column is recommended.
 *
 * Eight of the nine are things the user *ran into* — a cap, an empty allowance,
 * or a feature their tier does not carry. `browse` is the one that is not: the
 * settings page's "Upgrade" button, pressed by somebody who is not blocked by
 * anything. It exists because the alternative was raising the dialog with
 * `recipe-cap` and telling a user who had just pressed a button in Settings that
 * a recipe of theirs was not saved — copy that is false, and false in the
 * alarming direction.
 *
 * `coBrewers` and `feedback` are the features both paid tiers carry: inviting
 * somebody to edit a recipe (PLAN-COLLAB D-CO4), and tasting sheets. They
 * recommend Brewmaster, because the cheaper tier is enough for them.
 *
 * `telemetry` and `insights` are the Beerwright-only features, and they are
 * separate reasons rather than one because the headline states the fact that
 * was hit: "fermentation charts" and "your brewing trended" are different
 * sentences to somebody who just pressed a different button.
 */
export type UpgradeReason =
  | 'recipe-cap'
  | 'blend-cap'
  | 'journal-cap'
  | 'ai-allowance'
  | 'ai-locked'
  | 'telemetry'
  | 'insights'
  | 'feedback'
  | 'coBrewers'
  | 'browse';

/**
 * The tier to highlight for a trigger.
 *
 * Hitting the recipe or blend cap is a Brewmaster problem — recommending the
 * dearer tier for it would be an upsell the trigger does not support, and the
 * same goes for tasting sheets and co-brewers. The assistant, fermentation
 * charts and Insights are Beerwright problems, because Beerwright is the only
 * tier that carries them.
 * Somebody who is merely *looking* gets Brewmaster: with no trigger to justify
 * the dearer tier, the cheaper one is the honest recommendation and the table
 * beside it makes the case for the other.
 */
export function recommendedPlan(reason: UpgradeReason): Plan {
  switch (reason) {
    case 'ai-allowance':
    case 'ai-locked':
    case 'telemetry':
    case 'insights':
      return 'beerwright';
    default:
      return 'brewmaster';
  }
}

/** `September 2026` from a `YYYY-MM` key, for "your messages come back in …". */
export function monthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (match === null) return month;
  const year = Number(match[1]);
  const index = Number(match[2]) - 1;
  const name = MONTH_NAMES[index];
  return name === undefined ? month : `${name} ${year}`;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** The month *after* the one given, as a label. What "resets" means to a user. */
export function nextMonthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (match === null) return month;
  const year = Number(match[1]);
  const index = Number(match[2]);
  // A month outside 1–12 is not a month, so it is passed through rather than
  // rolled into a plausible-looking date the server never said.
  if (index < 1 || index > 12) return month;
  return index === 12 ? monthLabel(`${year + 1}-01`) : monthLabel(`${year}-${pad(index + 1)}`);
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}
